/**
 * VaxDueCard — homepage cards for upcoming/overdue vaccines from the booklet schedule.
 *
 * One stacked card per vaccine; each is dismissible per-tier (T-7 → T-1 → T-0).
 * Actions: Dismiss · Booked appointment · Reschedule · Done today.
 */

import { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, Check, Calendar, X, Syringe } from 'lucide-react';
import { format } from 'date-fns';
import { useBaby } from '@/contexts/BabyContext';
import { getVaxSchedule, updateVaxEntry } from '@/lib/vax-firestore';
import { decideAlert, tierLabel, type AlertDecision } from '@/lib/vaxSchedule';
import type { VaxScheduleEntry } from '@/types';

interface DueRow {
  entry: VaxScheduleEntry;
  decision: AlertDecision;
}

const VaxDueCard = () => {
  const { activeBaby } = useBaby();
  const [rows, setRows] = useState<DueRow[]>([]);
  const [showRescheduleFor, setShowRescheduleFor] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<string>('');

  const load = useCallback(async () => {
    if (!activeBaby?.id || !activeBaby.dob) return;
    const dob = new Date(activeBaby.dob);
    const all = await getVaxSchedule(activeBaby.id);
    const today = new Date();
    const due: DueRow[] = [];
    for (const entry of all) {
      const decision = decideAlert(entry, dob, today);
      if (decision.shouldShow) due.push({ entry, decision });
    }
    due.sort((a, b) => a.decision.daysUntil - b.decision.daysUntil);
    setRows(due);
  }, [activeBaby?.id, activeBaby?.dob]);

  useEffect(() => { load(); }, [load]);

  const handleDismiss = async (row: DueRow) => {
    const tier = row.decision.tier === 'overdue' ? 'T-0' : row.decision.tier;
    if (!tier || !activeBaby?.id) return;
    await updateVaxEntry(activeBaby.id, row.entry.id, {
      dismissedAt: new Date().toISOString(),
      dismissedTier: tier,
    });
    await load();
  };

  const handleBooked = async (row: DueRow) => {
    if (!activeBaby?.id) return;
    await updateVaxEntry(activeBaby.id, row.entry.id, {
      appointment: { status: 'booked', date: format(row.decision.effectiveDueDate, 'yyyy-MM-dd') },
    });
    await load();
  };

  const handleReschedule = async (row: DueRow) => {
    if (!activeBaby?.id || !rescheduleDate) return;
    await updateVaxEntry(activeBaby.id, row.entry.id, {
      appointment: { status: 'rescheduled', date: rescheduleDate },
      dismissedTier: undefined,
    });
    setShowRescheduleFor(null);
    setRescheduleDate('');
    await load();
  };

  const handleMarkDone = async (row: DueRow) => {
    if (!activeBaby?.id) return;
    await updateVaxEntry(activeBaby.id, row.entry.id, {
      status: 'given',
      givenDate: format(new Date(), 'yyyy-MM-dd'),
    });
    await load();
  };

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      {rows.map(row => {
        const isOverdue = row.decision.tier === 'overdue';
        const accent = isOverdue ? '--destructive' : '--feed-accent';
        const labelColor = `hsl(var(${accent}))`;
        const isReschedule = showRescheduleFor === row.entry.id;

        return (
          <div
            key={row.entry.id}
            className="rounded-2xl overflow-hidden"
            style={{ background: `hsl(var(${accent}) / 0.08)`, border: `1px solid hsl(var(${accent}) / 0.25)` }}
          >
            <div className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: `hsl(var(${accent}) / 0.15)` }}
                >
                  <ShieldAlert className="w-4 h-4" style={{ color: labelColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: labelColor }}>
                    {isOverdue ? 'Vaccine overdue' : 'Vaccine due'}
                    <span className="ml-2 text-muted-foreground font-medium normal-case tracking-normal">
                      {tierLabel(row.decision.tier, row.decision.daysUntil)}
                    </span>
                  </p>
                  <p className="text-sm font-semibold text-foreground mt-0.5">{row.entry.vaccineName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {row.entry.ageBucket} · {format(row.decision.effectiveDueDate, 'd MMM yyyy')}
                  </p>
                  {row.entry.appointment?.status === 'rescheduled' && row.entry.appointment.date && (
                    <p className="text-[11px] text-primary mt-0.5">
                      Rescheduled to {format(new Date(row.entry.appointment.date), 'd MMM yyyy')}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleDismiss(row)}
                  className="flex-shrink-0 w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>

              {isReschedule ? (
                <div className="mt-3 ml-12 flex items-center gap-2">
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-border bg-white"
                  />
                  <button
                    onClick={() => handleReschedule(row)}
                    disabled={!rescheduleDate}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground disabled:opacity-40"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setShowRescheduleFor(null); setRescheduleDate(''); }}
                    className="px-2 py-1.5 rounded-lg text-xs text-muted-foreground"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-3 ml-12 flex-wrap">
                  <button
                    onClick={() => handleBooked(row)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: 'hsl(var(--diaper-accent) / 0.15)', color: 'hsl(var(--diaper-accent))' }}
                  >
                    <Check className="w-3 h-3" />
                    Booked
                  </button>
                  <button
                    onClick={() => { setShowRescheduleFor(row.entry.id); setRescheduleDate(format(row.decision.effectiveDueDate, 'yyyy-MM-dd')); }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground"
                  >
                    <Calendar className="w-3 h-3" />
                    Reschedule
                  </button>
                  <button
                    onClick={() => handleMarkDone(row)}
                    className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}
                  >
                    <Syringe className="w-3 h-3" />
                    Done today
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VaxDueCard;
